import React, { useEffect, useState, useRef } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase-client";
import { ThemedText } from "@/components/ThemedText";
import { ProjectType } from "@/types/db/projects";
import { ClientType } from "@/types/db/clients";
import { ProjectCreateForm, ProjectCreateFormRef, ProjectPayload } from "@/components/screens/project/NewProjectForm";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { projectService } from "@/services/projectService";
import Head from "expo-router/head";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { useTheme } from "@/hooks/useTheme";
import { getSaveBehavior } from "@/lib/saveSettings";

type ProjectMeta = ProjectType["meta_data"];

export default function NewProjectPage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { triggerRefresh } = useRowSelectionContext();
  const [projectMeta, setProjectMeta] = useState<ProjectMeta | null>(null);
  const [localDraft, setLocalDraft] = useState<ProjectPayload>();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sidebarClient, setSidebarClient] = useState<ClientType | null>(null);
  const [projectName, setProjectName] = useState("");
  const [projectStatus, setProjectStatus] = useState("POTENTIAL");
  const formRef = useRef<ProjectCreateFormRef>(null);

  const handleCreateProject = async (payload: any) => {
    try {
      // Check if we received the full structure or just the unwrapped payload
      const hasProjectKey = payload.project !== undefined;
      const projectPayload = hasProjectKey ? payload.project : payload;
      const shiftsPayload = hasProjectKey ? payload.shifts : [];

      // Convert form data to service format
      const projectData = {
        name: projectPayload.name,
        status: projectPayload.status,
        client_id: projectPayload.client_id,
        meta_data: projectPayload.meta_data,
        // Contact and location fields
        requested_by: projectPayload.requested_by || undefined,
        primary_contact: projectPayload.primary_contact || undefined,
        onsite_contact: projectPayload.onsite_contact || undefined,
        location_id: projectPayload.location_id || undefined,
        shifts:
          shiftsPayload?.map((shift: any) => ({
            name: shift.name || undefined,
            description: shift.description || undefined,
            start_time: shift.start_time || undefined,
            end_time: shift.end_time || undefined,
            meta_data: shift.meta_data || {},
            positions: shift.positions || [],
          })) || [],
      };

      const newProject = await projectService.createProjectWithShifts(projectData);
      toast.showToast({ title: "Project created successfully", toastType: "success" });
      triggerRefresh("project");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("project");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newProject?.project?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create project",
        message: err.message,
        toastType: "error",
      });
    }
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data, error } = await supabase.from("tenants").select("meta_data->postions").single();
      if (error) throw error;
      setProjectMeta((data as any)?.meta_data ?? null);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load project settings.");
      toast.showToast({ title: "Failed to fetch users", message: e.message, toastType: "error" });
      setProjectMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync projectName and projectStatus with form state
  useEffect(() => {
    if (localDraft?.project?.name !== undefined && localDraft.project.name !== projectName) {
      setProjectName(localDraft.project.name);
    }
    if (localDraft?.project?.status !== undefined && localDraft.project.status !== projectStatus) {
      setProjectStatus(localDraft.project.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraft?.project?.name, localDraft?.project?.status]);

  if (loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>Loading project settings…</ThemedText>
      </ScrollView>
    );
  }

  // if (err || projectMeta == null) {
  //   return (
  //     <ScrollView contentContainerStyle={styles.centered}>
  //       <ThemedText type="defaultSemiBold">Something went wrong</ThemedText>
  //       <ThemedText style={{ opacity: 0.8, marginTop: 6 }}>{err ?? "Unknown error"}</ThemedText>
  //       <Button text="Retry" icon="refresh" style={{ marginTop: 12, borderRadius: 14, padding: 10 }} onPress={load} />
  //     </ScrollView>
  //   );
  // }

  const handleReset = () => {
    setProjectName("");
    setProjectStatus("POTENTIAL");
    setSidebarClient(null);
    formRef.current?.handleReset();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Projects</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="project"
          name={projectName}
          onNameChange={(name) => {
            setProjectName(name);
            formRef.current?.updateProjectName(name);
          }}
          namePlaceholder="Project name"
          status={projectStatus}
          onStatusChange={(status) => {
            setProjectStatus(status);
            formRef.current?.updateProjectStatus(status);
          }}
          statusOptions={[
            { text: "POTENTIAL", bgColor: colors.todo.border },
            { text: "PENDING APPROVAL", bgColor: colors.pending.border },
            { text: "IN PROGRESS", bgColor: colors.complete.border },
            { text: "ON HOLD", bgColor: colors.attention.border },
            { text: "CONFLICTING", bgColor: colors.neutral.border },
          ]}
          dirty={dirty}
          onSave={async () => {
            await formRef.current?.handleSave();
          }}
          onCancel={handleReset}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <ProjectCreateForm
            ref={formRef}
            initialProject={{ ...projectMeta, name: projectName, status: projectStatus }}
            onChange={(current) => {
              setLocalDraft(current);
            }}
            onSave={handleCreateProject}
            onDirtyChange={setDirty}
            onClientChange={setSidebarClient}
          />
        </View>
      </View>
    </>
  );
}

/** Presentational form (keeps its own local state) */

const styles = StyleSheet.create({
  centered: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 8,
  },
});
